import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Badge, TechStackBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Section, SectionHeader } from '../ui/Section';
import { AppData, featuredApps } from '../../data/apps';
import Icon from '@mdi/react';
import { mdiApple, mdiWeb, mdiArrowRight } from '@mdi/js';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AppCardLink = styled(Link)<{ $color: string }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
  background: var(--glass-background);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-card);
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${props => props.$color};
  }
`;

const AppHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
`;

const AppIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const AppLogo = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const AppInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AppName = styled.h3`
  font-size: var(--text-xl);
  margin-bottom: var(--space-1);
`;

const AppTagline = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const StatusBadge = styled(Badge)<{ $color: string }>`
  background: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const AppDescription = styled.p`
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
  flex: 1;
`;

const FeatureList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
`;

const AppLinks = styled.div`
  display: flex;
  gap: var(--space-3);
  margin-top: auto;
`;

const PlatformLink = styled.a`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-border);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  text-decoration: none;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--color-border-strong);
    color: var(--color-text-primary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ViewAllWrapper = styled.div`
  text-align: center;
  margin-top: var(--space-12);
`;

interface AppShowcaseProps {
  apps?: AppData[];
  showHeader?: boolean;
  showViewAll?: boolean;
  limit?: number;
}

const statusColors: Record<string, string> = {
  'live': 'var(--color-success)',
  'beta': 'var(--color-warning)',
  'coming-soon': 'var(--color-info)',
};

const statusLabels: Record<string, string> = {
  'live': 'Live',
  'beta': 'Beta',
  'coming-soon': 'Coming Soon',
};

export function AppShowcase({
  apps = featuredApps,
  showHeader = true,
  showViewAll = true,
  limit,
}: AppShowcaseProps) {
  const displayApps = limit ? apps.slice(0, limit) : apps;

  return (
    <Section background="secondary">
      {showHeader && (
        <SectionHeader
          title="Our App Ecosystem"
          subtitle="A suite of tools designed to simplify your digital life. Each app is crafted with care and built to work together."
          centered
        />
      )}

      <Grid>
        {displayApps.map(app => (
          <AppCardLink key={app.id} to={`/products/${app.id}`} $color={app.accentColor}>
            <AppHeader>
              <AppIcon $color={app.accentColor}>
                <AppLogo src={app.logo} alt={`${app.name} logo`} />
              </AppIcon>
              <AppInfo>
                <AppName>{app.name}</AppName>
                <AppTagline>{app.tagline}</AppTagline>
              </AppInfo>
              <StatusBadge $color={statusColors[app.status]} size="sm">
                {statusLabels[app.status]}
              </StatusBadge>
            </AppHeader>

            <AppDescription>{app.description}</AppDescription>

            <FeatureList>
              {app.features.slice(0, 3).map(feature => (
                <Badge key={feature} size="sm" variant="default">
                  {feature}
                </Badge>
              ))}
            </FeatureList>

            {app.techStack.length > 0 && (
              <TechStack>
                {app.techStack.map(tech => (
                  <TechStackBadge key={tech} tech={tech} />
                ))}
              </TechStack>
            )}

            <AppLinks>
              {app.links.web && (
                <PlatformLink as="span">
                  <Icon path={mdiWeb} />
                  Web App
                </PlatformLink>
              )}
              {app.links.appStore && app.links.appStore !== '#' && (
                <PlatformLink as="span">
                  <Icon path={mdiApple} />
                  App Store
                </PlatformLink>
              )}
            </AppLinks>
          </AppCardLink>
        ))}
      </Grid>

      {showViewAll && (
        <ViewAllWrapper>
          <Button as={Link} to="/products" variant="secondary">
            View All Products
            <Icon path={mdiArrowRight} size={0.8} />
          </Button>
        </ViewAllWrapper>
      )}
    </Section>
  );
}
